import React, { useState, useEffect, useMemo } from "react";
import "./TaxPricingRuleEditor.css";

// 1. Formula Types Definitions
const ALL_PRICING_RULE_TYPES = [
  {
    type: "property_class_rate",
    label: "बांधकाम प्रकारानुसार घरपट्टी",
    desc: "कच्चे, अर्धपक्के, भारवाहक, RCC आणि मोकळ्या भूखंडासाठी स्वतंत्र दर (दर शेकडा).",
    icon: "🏠",
    allowedSubjects: ["property"],
  },
  {
    type: "fixed",
    label: "निश्चित दर (Fixed Rate)",
    desc: "प्रत्येक खात्यासाठी एकच ठराविक वार्षिक रक्कम (उदा. पाणीपट्टी, दिवाबत्ती, परवाना फी).",
    icon: "₹",
    allowedSubjects: ["property", "business"],
  },
  {
    type: "percentage",
    label: "टक्केवारी (Percentage-wise)",
    desc: "मूल्य आधाराच्या (उदा. ARV, भांडवली मूल्य किंवा उलाढाल) ठराविक टक्केवारी.",
    icon: "%",
    allowedSubjects: ["property", "business"],
  },
  {
    type: "area_rate",
    label: "क्षेत्रफळानुसार दर (Area / Rate-wise)",
    desc: "प्रति चौ.फूट दर × क्षेत्रफळ × वापर/बांधकाम गुणक.",
    icon: "📐",
    allowedSubjects: ["property", "business"],
  },
  {
    type: "slab",
    label: "टप्पेनिहाय दर (Slab-based)",
    desc: "क्षेत्रफळ, भाडे मूल्य, भांडवली मूल्य किंवा उलाढालीच्या टप्प्यानुसार (Slab) वेगळे दर.",
    icon: "📊",
    allowedSubjects: ["property", "business"],
  },
  {
    type: "expression",
    label: "गणिती सूत्र (Formula Expression)",
    desc: "प्रगत सानुकूल गणिती सूत्र (Custom Math Formula).",
    icon: "🧮",
    allowedSubjects: ["property", "business"],
  },
];

// 2. Base Fields Metadata Helper
export const getBaseFieldMeta = (field) => {
  switch (field) {
    case "annual_rental_value":
    case "arv":
    case "annual_value":
      return {
        unit: "currency",
        symbol: "₹",
        nameMr: "रुपये",
        labelMr: "वार्षिक भाडे मूल्य (ARV)",
        placeholder: "उदा. ५०,०००",
        exampleBase: 80000,
      };
    case "assessed_value":
    case "capital_value":
      return {
        unit: "currency",
        symbol: "₹",
        nameMr: "रुपये",
        labelMr: "भांडवली / आकारणी मूल्य (Capital Value)",
        placeholder: "उदा. ५,००,०००",
        exampleBase: 500000,
      };
    case "annual_turnover":
    case "gross_income":
      return {
        unit: "currency",
        symbol: "₹",
        nameMr: "रुपये",
        labelMr: "वार्षिक उलाढाल (Turnover)",
        placeholder: "उदा. १०,००,०००",
        exampleBase: 750000,
      };
    case "built_up_area_sqft":
      return {
        unit: "area",
        symbol: "चौ.फूट",
        nameMr: "चौ.फूट",
        labelMr: "बांधकाम क्षेत्रफळ (Built-up Sq.Ft)",
        placeholder: "उदा. ५००",
        exampleBase: 850,
      };
    case "carpet_area_sqft":
      return {
        unit: "area",
        symbol: "चौ.फूट",
        nameMr: "चौ.फूट",
        labelMr: "कार्पेट क्षेत्रफळ (Carpet Sq.Ft)",
        placeholder: "उदा. ४००",
        exampleBase: 650,
      };
    case "land_area_sqft":
    case "plot_area_sqft":
      return {
        unit: "area",
        symbol: "चौ.फूट",
        nameMr: "चौ.फूट",
        labelMr: "जमीन / भूखंड क्षेत्रफळ (Land Sq.Ft)",
        placeholder: "उदा. १,०००",
        exampleBase: 1500,
      };
    case "premise_area_sqft":
      return {
        unit: "area",
        symbol: "चौ.फूट",
        nameMr: "चौ.फूट",
        labelMr: "व्यवसाय परिसर क्षेत्रफळ (Premise Sq.Ft)",
        placeholder: "उदा. ३००",
        exampleBase: 450,
      };
    case "signage_area_sqft":
      return {
        unit: "area",
        symbol: "चौ.फूट",
        nameMr: "चौ.फूट",
        labelMr: "जाहिरात फलक क्षेत्रफळ (Signage Sq.Ft)",
        placeholder: "उदा. ५०",
        exampleBase: 80,
      };
    case "employee_count":
    case "employees_count":
      return {
        unit: "count",
        symbol: "कर्मचारी",
        nameMr: "संख्या",
        labelMr: "कर्मचारी संख्या (Employee Count)",
        placeholder: "उदा. १०",
        exampleBase: 12,
      };
    case "active_location_count":
      return {
        unit: "count",
        symbol: "शाखा",
        nameMr: "संख्या",
        labelMr: "शाखा / युनिट संख्या",
        placeholder: "उदा. २",
        exampleBase: 3,
      };
    default:
      return {
        unit: "generic",
        symbol: "युनिट",
        nameMr: "युनिट",
        labelMr: field || "सानुकूल आधार",
        placeholder: "उदा. १००",
        exampleBase: 100,
      };
  }
};

// 3. Subject-Specific Field Lists
const PROPERTY_BASE_OPTIONS = [
  { value: "annual_rental_value", label: "वार्षिक भाडे मूल्य (ARV) — [रुपये ₹]" },
  { value: "assessed_value", label: "भांडवली / आकारणी मूल्य (Capital Value) — [रुपये ₹]" },
  { value: "built_up_area_sqft", label: "बांधकाम क्षेत्रफळ (Built-up Sq.Ft) — [चौ.फूट]" },
  { value: "carpet_area_sqft", label: "कार्पेट क्षेत्रफळ (Carpet Sq.Ft) — [चौ.फूट]" },
  { value: "land_area_sqft", label: "जमीन / भूखंड क्षेत्रफळ (Land Sq.Ft) — [चौ.फूट]" },
  { value: "custom", label: "सानुकूल (Custom Field)..." },
];

const BUSINESS_BASE_OPTIONS = [
  { value: "annual_turnover", label: "वार्षिक उलाढाल (Annual Turnover) — [रुपये ₹]" },
  { value: "premise_area_sqft", label: "व्यवसाय परिसर क्षेत्रफळ (Premise Sq.Ft) — [चौ.फूट]" },
  { value: "employee_count", label: "कर्मचारी संख्या (Employee Count) — [संख्या]" },
  { value: "signage_area_sqft", label: "जाहिरात फलक क्षेत्रफळ (Signage Sq.Ft) — [चौ.फूट]" },
  { value: "active_location_count", label: "शाखा / युनिट संख्या — [संख्या]" },
  { value: "custom", label: "सानुकूल (Custom Field)..." },
];

const PROPERTY_PERCENT_BASE_OPTIONS = [
  { value: "annual_rental_value", label: "वार्षिक भाडे मूल्य (ARV)" },
  { value: "assessed_value", label: "भांडवली / आकारणी मूल्य (Capital Value)" },
  { value: "custom", label: "सानुकूल (Custom Field)..." },
];

const BUSINESS_PERCENT_BASE_OPTIONS = [
  { value: "annual_turnover", label: "वार्षिक उलाढाल (Annual Turnover)" },
  { value: "custom", label: "सानुकूल (Custom Field)..." },
];

const PROPERTY_AREA_OPTIONS = [
  { value: "built_up_area_sqft", label: "बांधकाम क्षेत्रफळ (Built-up Sq.Ft)" },
  { value: "carpet_area_sqft", label: "कार्पेट क्षेत्रफळ (Carpet Sq.Ft)" },
  { value: "land_area_sqft", label: "जमीन / भूखंड क्षेत्रफळ (Land Area Sq.Ft)" },
  { value: "custom", label: "सानुकूल (Custom Field)..." },
];

const BUSINESS_AREA_OPTIONS = [
  { value: "premise_area_sqft", label: "व्यवसाय परिसर क्षेत्रफळ (Premise Area Sq.Ft)" },
  { value: "signage_area_sqft", label: "जाहिरात फलक / बोर्ड क्षेत्रफळ (Signage Sq.Ft)" },
  { value: "custom", label: "सानुकूल (Custom Field)..." },
];

const MULTIPLIER_FIELD_OPTIONS = [
  { value: "", label: "गुणक नाही (सरळ दर)" },
  { value: "usage_factor", label: "वापर प्रकार गुणक (Usage Factor)" },
  { value: "construction_factor", label: "बांधकाम प्रकार गुणक (Construction Factor)" },
  { value: "age_factor", label: "इमारतीचे वयोमान गुणक (Age Factor)" },
  { value: "custom", label: "सानुकूल (Custom Field)..." },
];

export default function TaxPricingRuleEditor({
  initialType = "fixed",
  initialConfig = {},
  subjectType = "property",
}) {
  const isBusiness = subjectType === "business";

  // Filter formula types: property_class_rate is not applicable to businesses
  const availableFormulaTypes = useMemo(() => {
    return ALL_PRICING_RULE_TYPES.filter((r) =>
      r.allowedSubjects.includes(subjectType)
    );
  }, [subjectType]);

  const [formulaType, setFormulaType] = useState(() => {
    if (isBusiness && initialType === "property_class_rate") {
      return "fixed";
    }
    return initialType || "fixed";
  });

  const [isRawJsonMode, setIsRawJsonMode] = useState(false);

  // 1. Fixed Amount State
  const [fixedAmount, setFixedAmount] = useState(
    initialConfig?.amount !== undefined ? initialConfig.amount : (isBusiness ? 500 : 100)
  );

  // 2. Property Class Rates State
  const [propertyClassRates, setPropertyClassRates] = useState({
    kachha: initialConfig?.rates?.kachha ?? 0.20,
    semi_paka: initialConfig?.rates?.semi_paka ?? 0.35,
    load_bearing: initialConfig?.rates?.load_bearing ?? 0.50,
    rcc: initialConfig?.rates?.rcc ?? 0.75,
    open_plot: initialConfig?.rates?.open_plot ?? 0.25,
  });

  // 3. Percentage State
  const percentBaseOptions = isBusiness ? BUSINESS_PERCENT_BASE_OPTIONS : PROPERTY_PERCENT_BASE_OPTIONS;
  const defaultPercentBase = isBusiness ? "annual_turnover" : "annual_rental_value";

  const [percentageBase, setPercentageBase] = useState(() => {
    const field = initialConfig?.base_field || defaultPercentBase;
    return percentBaseOptions.some((o) => o.value === field) ? field : "custom";
  });
  const [customPercentageBase, setCustomPercentageBase] = useState(() => {
    const field = initialConfig?.base_field || defaultPercentBase;
    return percentBaseOptions.some((o) => o.value === field) ? "" : field;
  });
  const [ratePercent, setRatePercent] = useState(
    initialConfig?.rate_percent !== undefined ? initialConfig.rate_percent : (isBusiness ? 0.5 : 8)
  );

  // 4. Area Rate State
  const areaOptions = isBusiness ? BUSINESS_AREA_OPTIONS : PROPERTY_AREA_OPTIONS;
  const defaultAreaField = isBusiness ? "premise_area_sqft" : "built_up_area_sqft";

  const [areaField, setAreaField] = useState(() => {
    const field = initialConfig?.area_field || defaultAreaField;
    return areaOptions.some((o) => o.value === field) ? field : "custom";
  });
  const [customAreaField, setCustomAreaField] = useState(() => {
    const field = initialConfig?.area_field || defaultAreaField;
    return areaOptions.some((o) => o.value === field) ? "" : field;
  });
  const [ratePerUnit, setRatePerUnit] = useState(
    initialConfig?.rate_per_unit !== undefined ? initialConfig.rate_per_unit : 2.5
  );
  const [multiplierField, setMultiplierField] = useState(() => {
    const field = initialConfig?.multiplier_field || "";
    return MULTIPLIER_FIELD_OPTIONS.some((o) => o.value === field) ? field : "custom";
  });
  const [customMultiplierField, setCustomMultiplierField] = useState(() => {
    const field = initialConfig?.multiplier_field || "";
    return MULTIPLIER_FIELD_OPTIONS.some((o) => o.value === field) ? "" : field;
  });

  // 5. Slab State
  const slabBaseOptions = isBusiness ? BUSINESS_BASE_OPTIONS : PROPERTY_BASE_OPTIONS;
  const defaultSlabBase = isBusiness ? "annual_turnover" : "annual_rental_value";

  const [slabBaseField, setSlabBaseField] = useState(() => {
    const field = initialConfig?.base_field || defaultSlabBase;
    return slabBaseOptions.some((o) => o.value === field) ? field : "custom";
  });
  const [customSlabBase, setCustomSlabBase] = useState(() => {
    const field = initialConfig?.base_field || defaultSlabBase;
    return slabBaseOptions.some((o) => o.value === field) ? "" : field;
  });

  const effectiveSlabBase = slabBaseField === "custom" ? customSlabBase.trim() || "custom" : slabBaseField;
  const baseMeta = useMemo(() => getBaseFieldMeta(effectiveSlabBase), [effectiveSlabBase]);

  // Allowed rate types based on base field unit
  const allowedRateTypes = useMemo(() => {
    if (baseMeta.unit === "currency") {
      return [
        { value: "rate_percent", label: "टक्केवारी (%)", symbol: "%" },
        { value: "amount", label: "निश्चित रक्कम (₹)", symbol: "₹" },
      ];
    }
    if (baseMeta.unit === "area") {
      return [
        { value: "rate_per_unit", label: "दर प्रति चौ.फूट (₹/Sq.Ft)", symbol: "₹/चौ.फूट" },
        { value: "amount", label: "निश्चित रक्कम (₹)", symbol: "₹" },
      ];
    }
    if (baseMeta.unit === "count") {
      return [
        { value: "amount", label: "निश्चित रक्कम (₹)", symbol: "₹" },
        { value: "rate_per_unit", label: "दर प्रति घटक/कर्मचारी (₹)", symbol: "₹/नग" },
      ];
    }
    return [
      { value: "rate_per_unit", label: "दर प्रति युनिट (₹/Unit)", symbol: "₹/युनिट" },
      { value: "rate_percent", label: "टक्केवारी (%)", symbol: "%" },
      { value: "amount", label: "निश्चित रक्कम (₹)", symbol: "₹" },
    ];
  }, [baseMeta.unit]);

  const [slabMode, setSlabMode] = useState(initialConfig?.mode || "progressive");

  // Initial slabs creation
  const [slabs, setSlabs] = useState(() => {
    if (Array.isArray(initialConfig?.slabs) && initialConfig.slabs.length > 0) {
      return initialConfig.slabs.map((s) => ({
        up_to: s.up_to === null || s.up_to === undefined ? "" : String(s.up_to),
        type:
          s.rate_percent !== null && s.rate_percent !== undefined
            ? "rate_percent"
            : s.rate_per_unit !== null && s.rate_per_unit !== undefined
            ? "rate_per_unit"
            : "amount",
        value:
          s.rate_percent !== null && s.rate_percent !== undefined
            ? String(s.rate_percent)
            : s.rate_per_unit !== null && s.rate_per_unit !== undefined
            ? String(s.rate_per_unit)
            : String(s.amount || 0),
      }));
    }
    // Default sensible slabs according to base field unit
    if (baseMeta.unit === "currency") {
      return [
        { up_to: "50000", type: "rate_percent", value: "5.00" },
        { up_to: "100000", type: "rate_percent", value: "8.00" },
        { up_to: "", type: "rate_percent", value: "12.00" },
      ];
    }
    if (baseMeta.unit === "area") {
      return [
        { up_to: "500", type: "rate_per_unit", value: "1.50" },
        { up_to: "1000", type: "rate_per_unit", value: "2.50" },
        { up_to: "", type: "rate_per_unit", value: "3.50" },
      ];
    }
    return [
      { up_to: "5", type: "amount", value: "500" },
      { up_to: "20", type: "amount", value: "1200" },
      { up_to: "", type: "amount", value: "2500" },
    ];
  });

  // Automatically sanitize slab row rate types if base field changes
  useEffect(() => {
    const validValues = allowedRateTypes.map((t) => t.value);
    setSlabs((currentSlabs) =>
      currentSlabs.map((s) => {
        if (!validValues.includes(s.type)) {
          return { ...s, type: validValues[0] };
        }
        return s;
      })
    );
  }, [allowedRateTypes]);

  // 6. Expression State
  const defaultExpression = isBusiness
    ? "annual_turnover * 0.005"
    : "built_up_area_sqft * 2.5";
  const [expression, setExpression] = useState(
    initialConfig?.expression || defaultExpression
  );

  // 7. Raw JSON Fallback State
  const [rawJsonText, setRawJsonText] = useState(() =>
    JSON.stringify(initialConfig || { amount: 100 }, null, 2)
  );

  // 8. Interactive Simulator State
  const [simulatorInput, setSimulatorInput] = useState(String(baseMeta.exampleBase));

  useEffect(() => {
    setSimulatorInput(String(baseMeta.exampleBase));
  }, [baseMeta.exampleBase]);

  // Compute final formula_config JSON
  const computeFormulaConfig = () => {
    if (isRawJsonMode) {
      try {
        return JSON.parse(rawJsonText);
      } catch {
        return initialConfig || {};
      }
    }

    if (formulaType === "fixed") {
      return { amount: Number(fixedAmount) || 0 };
    }
    if (formulaType === "property_class_rate") {
      return {
        base_field: initialConfig?.base_field || "assessed_value",
        class_field: "construction_type",
        rate_mode: "per_100",
        rates: Object.fromEntries(
          Object.entries(propertyClassRates).map(([key, value]) => [
            key,
            Number(value) || 0,
          ])
        ),
      };
    }
    if (formulaType === "percentage") {
      const base = percentageBase === "custom" ? customPercentageBase.trim() : percentageBase;
      return {
        base_field: base || defaultPercentBase,
        rate_percent: Number(ratePercent) || 0,
      };
    }
    if (formulaType === "area_rate") {
      const area = areaField === "custom" ? customAreaField.trim() : areaField;
      const mult = multiplierField === "custom" ? customMultiplierField.trim() : multiplierField;
      const config = {
        area_field: area || defaultAreaField,
        rate_per_unit: Number(ratePerUnit) || 0,
      };
      if (mult) config.multiplier_field = mult;
      return config;
    }
    if (formulaType === "slab") {
      const formattedSlabs = slabs.map((s) => {
        const upTo = s.up_to === "" || s.up_to === null ? null : Number(s.up_to);
        const val = Number(s.value) || 0;
        return {
          up_to: upTo,
          rate_per_unit: s.type === "rate_per_unit" ? val : null,
          rate_percent: s.type === "rate_percent" ? val : null,
          amount: s.type === "amount" ? val : null,
        };
      });
      return {
        base_field: effectiveSlabBase,
        mode: slabMode,
        slabs: formattedSlabs,
      };
    }
    if (formulaType === "expression") {
      return { expression: expression.trim() };
    }
    return { amount: 100 };
  };

  const calculatedConfig = computeFormulaConfig();
  const jsonString = JSON.stringify(calculatedConfig, null, 2);

  // Sync to raw text when visual changes occur
  useEffect(() => {
    if (!isRawJsonMode) {
      setRawJsonText(jsonString);
    }
  }, [jsonString, isRawJsonMode]);

  // Handlers for Slab Table
  const handleAddSlab = () => {
    const defaultType = allowedRateTypes[0].value;
    const lastSlab = slabs[slabs.length - 1];
    let defaultUpTo = "";
    if (lastSlab && lastSlab.up_to !== "") {
      defaultUpTo = String(Number(lastSlab.up_to) * 2);
    }
    setSlabs([...slabs, { up_to: defaultUpTo, type: defaultType, value: "2.00" }]);
  };

  const handleRemoveSlab = (index) => {
    if (slabs.length <= 1) return;
    setSlabs(slabs.filter((_, i) => i !== index));
  };

  const handleUpdateSlab = (index, field, val) => {
    const next = [...slabs];
    next[index] = { ...next[index], [field]: val };
    setSlabs(next);
  };

  // Helper for adding variable to expression
  const insertVariable = (varName) => {
    setExpression((prev) => (prev ? `${prev} * ${varName}` : varName));
  };

  // Inline Slab Sequence Validation
  const slabValidation = useMemo(() => {
    if (formulaType !== "slab") return null;
    let prev = 0;
    for (let i = 0; i < slabs.length; i++) {
      const upTo = slabs[i].up_to;
      const isLast = i === slabs.length - 1;
      if (upTo === "" || upTo === null) {
        if (!isLast) {
          return {
            isValid: false,
            message: `⚠️ टप्पा #${i + 1} अमर्याद ठेवता येणार नाही. केवळ अंतिम टप्पा अमर्याद असू शकतो.`,
          };
        }
      } else {
        const num = Number(upTo);
        if (num <= 0) {
          return {
            isValid: false,
            message: `⚠️ टप्पा #${i + 1} ची कमाल मर्यादा शून्यापेक्षा जास्त असावी.`,
          };
        }
        if (num <= prev) {
          return {
            isValid: false,
            message: `⚠️ टप्पा #${i + 1} ची कमाल मर्यादा (${num}) ही मागील टप्प्यापेक्षा (${prev}) मोठी असावी (चढता क्रम आवश्यक).`,
          };
        }
        prev = num;
      }
    }
    return { isValid: true };
  }, [formulaType, slabs]);

  // Client-Side Live Tax Simulation Engine
  const simulationResult = useMemo(() => {
    if (formulaType !== "slab" || !slabValidation?.isValid) return null;
    const testVal = Number(simulatorInput);
    if (!Number.isFinite(testVal) || testVal < 0) return null;

    try {
      if (slabMode === "flat") {
        const selected = slabs.find((s) => s.up_to === "" || testVal <= Number(s.up_to));
        if (!selected) return { total: 0, steps: ["कोणत्याही टप्प्यात बसत नाही"] };
        const val = Number(selected.value) || 0;
        let amount = 0;
        let desc = "";
        if (selected.type === "amount") {
          amount = val;
          desc = `ठोक रक्कम: ₹${amount.toLocaleString("en-IN")}`;
        } else if (selected.type === "rate_percent") {
          amount = (testVal * val) / 100;
          desc = `संपूर्ण ${testVal.toLocaleString("en-IN")} च्या ${val}% = ₹${amount.toLocaleString("en-IN")}`;
        } else {
          amount = testVal * val;
          desc = `संपूर्ण ${testVal.toLocaleString("en-IN")} × ₹${val} = ₹${amount.toLocaleString("en-IN")}`;
        }
        return {
          total: Math.round(amount),
          steps: [`लागू टप्पा (${selected.up_to ? `कमाल ${selected.up_to}` : "अमर्याद"}): ${desc}`],
        };
      }

      // Progressive Mode
      let previousUpper = 0;
      let total = 0;
      const steps = [];

      for (let i = 0; i < slabs.length; i++) {
        if (testVal <= previousUpper) break;
        const slab = slabs[i];
        const upper = slab.up_to === "" ? testVal : Math.min(testVal, Number(slab.up_to));
        const units = Math.max(0, upper - previousUpper);
        const val = Number(slab.value) || 0;
        let stepAmount = 0;
        let stepDesc = "";

        if (slab.type === "amount") {
          stepAmount = units > 0 ? val : 0;
          stepDesc = `टप्पा #${i + 1} (${previousUpper} ते ${upper}): निश्चित रक्कम = ₹${stepAmount}`;
        } else if (slab.type === "rate_percent") {
          stepAmount = (units * val) / 100;
          stepDesc = `टप्पा #${i + 1} (${previousUpper} ते ${upper}): ${units.toLocaleString("en-IN")} च्या ${val}% = ₹${stepAmount.toFixed(2)}`;
        } else {
          stepAmount = units * val;
          stepDesc = `टप्पा #${i + 1} (${previousUpper} ते ${upper}): ${units.toLocaleString("en-IN")} × ₹${val} = ₹${stepAmount.toFixed(2)}`;
        }

        total += stepAmount;
        steps.push(stepDesc);
        previousUpper = upper;
      }

      return { total: Math.round(total), steps };
    } catch {
      return null;
    }
  }, [formulaType, slabValidation, slabs, slabMode, simulatorInput]);

  return (
    <div className="tpr-editor-container">
      {/* Hidden Form Inputs for Native FormData Submission */}
      <input type="hidden" name="formula_type" value={formulaType} />
      <textarea
        name="formula_config"
        value={jsonString}
        readOnly
        style={{ display: "none" }}
      />

      <div className="tpr-header-bar">
        <div>
          <h4 className="tpr-section-title">
            कर आकारणी सूत्र व दर रचना (Pricing Rule Config)
            <span className="tpr-badge-domain">
              {isBusiness ? "🏢 व्यावसायिक आस्थापना" : "🏠 निवासी / मालमत्ता"}
            </span>
          </h4>
          <p className="tpr-section-sub">
            ग्रामपंचायत ठरावानुसार सुलभ आकारणी पद्धत निवडा व दर संरचना तयार करा.
          </p>
        </div>
        <button
          type="button"
          className="tpr-btn-toggle-json"
          onClick={() => setIsRawJsonMode(!isRawJsonMode)}
        >
          {isRawJsonMode ? "📋 व्हिज्युअल फॉर्म" : "⚙️ प्रगत JSON पद्धत"}
        </button>
      </div>

      {/* Pricing Rule Selector Form Input */}
      <div className="tpr-selector-box">
        <label className="tpr-select-label">
          <span>कर आकारणी पद्धत (Pricing Rule / Formula Type) *</span>
          <select
            className="tpr-select-input"
            value={formulaType}
            onChange={(e) => setFormulaType(e.target.value)}
          >
            {availableFormulaTypes.map((rule) => (
              <option key={rule.type} value={rule.type}>
                {rule.icon} {rule.label} — ({rule.desc})
              </option>
            ))}
          </select>
        </label>
        <div className="tpr-selected-hint">
          💡 <strong>{availableFormulaTypes.find((r) => r.type === formulaType)?.label}:</strong>{" "}
          {availableFormulaTypes.find((r) => r.type === formulaType)?.desc}
        </div>
      </div>

      {/* Rule Parameter Config Form */}
      {isRawJsonMode ? (
        <div className="tpr-form-box">
          <label className="tpr-label">
            प्रगत JSON संरचना (Raw JSON Config)
            <textarea
              className="tpr-textarea-code"
              rows={8}
              value={rawJsonText}
              onChange={(e) => setRawJsonText(e.target.value)}
            />
          </label>
        </div>
      ) : (
        <div className="tpr-form-box">
          {/* 1. PROPERTY CLASS RATE FORM */}
          {formulaType === "property_class_rate" && (
            <div className="tpr-fields-group">
              <div className="tpr-preview-banner">
                GP ठरावानुसार प्रत्येक ₹100 भांडवली/आकारणी मूल्यावरील वार्षिक दर (दर शेकडा) नोंदवा.
              </div>
              <div className="tpr-grid-3">
                {[
                  ["kachha", "कच्चे / झोपडी"],
                  ["semi_paka", "अर्धपक्के / पत्रा"],
                  ["load_bearing", "पक्के / Load bearing"],
                  ["rcc", "RCC"],
                  ["open_plot", "मोकळा भूखंड"],
                ].map(([key, label]) => (
                  <label className="tpr-label" key={key}>
                    {label} (₹ प्रति ₹100)
                    <input
                      type="number"
                      min="0"
                      max="100"
                      step="0.01"
                      className="tpr-input"
                      value={propertyClassRates[key]}
                      onChange={(event) =>
                        setPropertyClassRates((current) => ({
                          ...current,
                          [key]: event.target.value,
                        }))
                      }
                      required
                    />
                  </label>
                ))}
              </div>
            </div>
          )}

          {/* 2. FIXED AMOUNT FORM */}
          {formulaType === "fixed" && (
            <div className="tpr-fields-group">
              <label className="tpr-label">
                निश्चित आकारणी रक्कम (Fixed Amount in ₹) *
                <div className="tpr-input-prefix-wrap">
                  <span className="tpr-prefix">₹</span>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    className="tpr-input"
                    value={fixedAmount}
                    onChange={(e) => setFixedAmount(e.target.value)}
                    required
                  />
                </div>
              </label>
              <div className="tpr-preview-banner">
                💡 <strong>Preview:</strong> या सूत्राद्वारे प्रत्येक पात्र खात्यावर दर आकारणीच्या वेळी थेट{" "}
                <mark>₹{Number(fixedAmount || 0).toLocaleString("en-IN")}</mark> वार्षिक/नियतकालिक कर लागू होईल.
              </div>
            </div>
          )}

          {/* 3. PERCENTAGE FORM */}
          {formulaType === "percentage" && (
            <div className="tpr-fields-group">
              <div className="tpr-grid-2">
                <label className="tpr-label">
                  मूल्य आधार (Base Value Field) *
                  <select
                    className="tpr-select"
                    value={percentageBase}
                    onChange={(e) => setPercentageBase(e.target.value)}
                  >
                    {percentBaseOptions.map((o) => (
                      <option key={o.value} value={o.value}>
                        {o.label}
                      </option>
                    ))}
                  </select>
                </label>

                {percentageBase === "custom" && (
                  <label className="tpr-label">
                    सानुकूल फील्ड नाव (Custom Field Name) *
                    <input
                      type="text"
                      className="tpr-input"
                      placeholder="उदा. annual_rental_value"
                      value={customPercentageBase}
                      onChange={(e) => setCustomPercentageBase(e.target.value)}
                      required
                    />
                  </label>
                )}

                <label className="tpr-label">
                  कराची टक्केवारी (Rate Percent %) *
                  <div className="tpr-input-prefix-wrap">
                    <input
                      type="number"
                      min="0"
                      max="100"
                      step="0.01"
                      className="tpr-input"
                      value={ratePercent}
                      onChange={(e) => setRatePercent(e.target.value)}
                      required
                    />
                    <span className="tpr-suffix">%</span>
                  </div>
                </label>
              </div>

              <div className="tpr-preview-banner">
                💡 <strong>आकारणी सूत्र:</strong> कराची रक्कम = (
                <code>
                  {percentageBase === "custom"
                    ? customPercentageBase || "field"
                    : percentageBase}
                </code>
                ) × <mark>{ratePercent || 0}%</mark>
              </div>
            </div>
          )}

          {/* 4. AREA RATE FORM */}
          {formulaType === "area_rate" && (
            <div className="tpr-fields-group">
              <div className="tpr-grid-3">
                <label className="tpr-label">
                  क्षेत्रफळ क्षेत्र (Area Field) *
                  <select
                    className="tpr-select"
                    value={areaField}
                    onChange={(e) => setAreaField(e.target.value)}
                  >
                    {areaOptions.map((o) => (
                      <option key={o.value} value={o.value}>
                        {o.label}
                      </option>
                    ))}
                  </select>
                </label>

                {areaField === "custom" && (
                  <label className="tpr-label">
                    सानुकूल क्षेत्रफळ नाव *
                    <input
                      type="text"
                      className="tpr-input"
                      placeholder="premise_area_sqft"
                      value={customAreaField}
                      onChange={(e) => setCustomAreaField(e.target.value)}
                      required
                    />
                  </label>
                )}

                <label className="tpr-label">
                  प्रति चौ.फूट दर (Rate Per Sq.Ft ₹) *
                  <div className="tpr-input-prefix-wrap">
                    <span className="tpr-prefix">₹</span>
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      className="tpr-input"
                      value={ratePerUnit}
                      onChange={(e) => setRatePerUnit(e.target.value)}
                      required
                    />
                  </div>
                </label>

                <label className="tpr-label">
                  गुणक घटक (Multiplier Field - ऐच्छिक)
                  <select
                    className="tpr-select"
                    value={multiplierField}
                    onChange={(e) => setMultiplierField(e.target.value)}
                  >
                    {MULTIPLIER_FIELD_OPTIONS.map((o) => (
                      <option key={o.value} value={o.value}>
                        {o.label}
                      </option>
                    ))}
                  </select>
                </label>

                {multiplierField === "custom" && (
                  <label className="tpr-label">
                    सानुकूल गुणक नाव *
                    <input
                      type="text"
                      className="tpr-input"
                      placeholder="usage_factor"
                      value={customMultiplierField}
                      onChange={(e) => setCustomMultiplierField(e.target.value)}
                    />
                  </label>
                )}
              </div>

              <div className="tpr-preview-banner">
                💡 <strong>आकारणी सूत्र:</strong> कराची रक्कम = (
                <code>{areaField === "custom" ? customAreaField || "area" : areaField}</code>
                )
                {multiplierField && (
                  <>
                    {" "}
                    × (
                    <code>
                      {multiplierField === "custom"
                        ? customMultiplierField || "multiplier"
                        : multiplierField}
                    </code>
                    )
                  </>
                )}{" "}
                × <mark>₹{ratePerUnit || 0} / चौ.फूट</mark>
              </div>
            </div>
          )}

          {/* 5. SLAB FORM (PROFESSIONAL CA-GRADE RECTIFIED) */}
          {formulaType === "slab" && (
            <div className="tpr-fields-group">
              <div className="tpr-grid-2">
                <label className="tpr-label">
                  मूल्य आधार (Base Field) *
                  <select
                    className="tpr-select"
                    value={slabBaseField}
                    onChange={(e) => setSlabBaseField(e.target.value)}
                  >
                    {slabBaseOptions.map((o) => (
                      <option key={o.value} value={o.value}>
                        {o.label}
                      </option>
                    ))}
                  </select>
                </label>

                <label className="tpr-label">
                  गणना पद्धत (Slab Calculation Mode) *
                  <select
                    className="tpr-select"
                    value={slabMode}
                    onChange={(e) => setSlabMode(e.target.value)}
                  >
                    <option value="progressive">
                      टप्प्यानिहाय उत्तरोत्तर दर (Progressive Slabs — जसे आयकर / दर टप्पा)
                    </option>
                    <option value="flat">
                      संपूर्ण मूल्यावर एकाच टप्प्याचा दर (Flat Bracket Slab — जसे व्यवसाय कर / परवाना फी)
                    </option>
                  </select>
                </label>
              </div>

              {slabBaseField === "custom" && (
                <label className="tpr-label" style={{ maxWidth: "420px" }}>
                  सानुकूल आधार फील्ड नाव (Custom Base Field) *
                  <input
                    type="text"
                    className="tpr-input"
                    placeholder="उदा. annual_turnover किंवा carpet_area_sqft"
                    value={customSlabBase}
                    onChange={(e) => setCustomSlabBase(e.target.value)}
                    required
                  />
                </label>
              )}

              {/* Mode Educational Banner */}
              <div className="tpr-mode-info-banner">
                <div className="tpr-mode-text">
                  {slabMode === "progressive" ? (
                    <>
                      📊 <strong>प्रोग्रेसिव्ह पद्धत (Progressive):</strong> प्रत्येक टप्प्यातील रकमेवर/क्षेत्रफळावर त्या त्या टप्प्याचा दर स्वतंत्रपणे आकारला जातो.
                      (उदा. ०-५०० वरील दर + पुढील टप्प्यावरील दर).
                    </>
                  ) : (
                    <>
                      🎯 <strong>फ्लॅट ब्रॅकेट पद्धत (Flat Bracket):</strong> एकूण मूल्य ज्या स्लॅबमध्ये बसेल, त्या संपूर्ण मूल्यावर फक्त त्या एकाच टप्प्याचा दर किंवा ठोक रक्कम आकारली जाते.
                    </>
                  )}
                </div>
                <div className="tpr-base-unit-tag">
                  आधार परिमाण: <strong>{baseMeta.labelMr}</strong> [{baseMeta.symbol}]
                </div>
              </div>

              {/* Slab Sequence Error Banner if any */}
              {slabValidation && !slabValidation.isValid && (
                <div className="tpr-validation-alert">
                  {slabValidation.message}
                </div>
              )}

              {/* Slabs Table Header & Action */}
              <div className="tpr-slabs-header">
                <div>
                  <strong>टप्पेनिहाय स्लॅब वेळापत्रक (Slab Rates Schedule)</strong>
                  <div className="tpr-slabs-sub">
                    {baseMeta.unit === "currency"
                      ? "चलन मूल्यावर आधारित असल्याने टक्केवारी (%) किंवा निश्चित रक्कम (₹) लागू होते."
                      : baseMeta.unit === "area"
                      ? "क्षेत्रफळावर आधारित असल्याने दर प्रति चौ.फूट (₹/Sq.Ft) किंवा निश्चित रक्कम (₹) लागू होते."
                      : "घटक संख्येवर आधारित असल्याने निश्चित रक्कम (₹) किंवा प्रति नग दर लागू होतो."}
                  </div>
                </div>
                <button
                  type="button"
                  className="tpr-btn-add-slab"
                  onClick={handleAddSlab}
                >
                  + नवीन टप्पा जोडा
                </button>
              </div>

              {/* Responsive Slabs Table */}
              <div className="tpr-slabs-table-wrap">
                <table className="tpr-slabs-table">
                  <thead>
                    <tr>
                      <th style={{ width: "55px" }}>टप्पा</th>
                      <th style={{ minWidth: "220px" }}>
                        {baseMeta.unit === "currency"
                          ? "कमाल मर्यादा (रुपये ₹)"
                          : baseMeta.unit === "area"
                          ? "कमाल क्षेत्रफळ (चौ.फूट)"
                          : "कमाल मर्यादा (संख्या मर्यादा)"}
                      </th>
                      <th style={{ minWidth: "200px" }}>दर प्रकार (Rate Type)</th>
                      <th style={{ minWidth: "160px" }}>दर / रक्कम (Value)</th>
                      <th style={{ width: "50px", textAlign: "center" }}>कृती</th>
                    </tr>
                  </thead>
                  <tbody>
                    {slabs.map((slab, idx) => {
                      const isLast = idx === slabs.length - 1;
                      const activeTypeObj =
                        allowedRateTypes.find((t) => t.value === slab.type) || allowedRateTypes[0];

                      return (
                        <tr key={idx}>
                          <td>
                            <strong>#{idx + 1}</strong>
                          </td>
                          <td>
                            <div className="tpr-slab-upto-wrap">
                              <div className="tpr-input-prefix-wrap">
                                {baseMeta.unit === "currency" && <span className="tpr-prefix">₹</span>}
                                <input
                                  type="number"
                                  min="0"
                                  step="any"
                                  className="tpr-input"
                                  placeholder={isLast ? "अमर्याद (Open Ended)" : baseMeta.placeholder}
                                  value={slab.up_to}
                                  onChange={(e) =>
                                    handleUpdateSlab(idx, "up_to", e.target.value)
                                  }
                                />
                                {baseMeta.unit === "area" && <span className="tpr-suffix">चौ.फूट</span>}
                              </div>
                              {isLast && (
                                <small className="tpr-hint-text">
                                  (शेवटचा टप्पा: रिकामे ठेवा = अमर्याद / पुढील सर्व)
                                </small>
                              )}
                            </div>
                          </td>
                          <td>
                            <select
                              className="tpr-select-sm"
                              value={slab.type}
                              onChange={(e) =>
                                handleUpdateSlab(idx, "type", e.target.value)
                              }
                            >
                              {allowedRateTypes.map((opt) => (
                                <option key={opt.value} value={opt.value}>
                                  {opt.label}
                                </option>
                              ))}
                            </select>
                          </td>
                          <td>
                            <div className="tpr-input-prefix-wrap">
                              {activeTypeObj.symbol === "₹" && <span className="tpr-prefix">₹</span>}
                              <input
                                type="number"
                                step="0.01"
                                min="0"
                                className="tpr-input"
                                value={slab.value}
                                onChange={(e) =>
                                  handleUpdateSlab(idx, "value", e.target.value)
                                }
                                required
                              />
                              {activeTypeObj.symbol !== "₹" && (
                                <span className="tpr-suffix">{activeTypeObj.symbol}</span>
                              )}
                            </div>
                          </td>
                          <td style={{ textAlign: "center" }}>
                            <button
                              type="button"
                              className="tpr-btn-remove-slab"
                              onClick={() => handleRemoveSlab(idx)}
                              disabled={slabs.length <= 1}
                              title="टप्पा हटवा"
                            >
                              ×
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {/* Interactive Live Tax Calculation Simulator */}
              <div className="tpr-simulator-box">
                <div className="tpr-sim-header">
                  <span>🧪 <strong>थेट कर आकारणी सिम्युलेटर (Live Tax Simulation)</strong></span>
                  <span className="tpr-sim-hint">
                    खालील नमुना मूल्य बदलून प्रत्यक्ष कर रकमेची पडताळणी करा
                  </span>
                </div>
                <div className="tpr-sim-row">
                  <label className="tpr-sim-input-label">
                    नमुना {baseMeta.labelMr}:
                    <div className="tpr-input-prefix-wrap" style={{ width: "200px" }}>
                      {baseMeta.unit === "currency" && <span className="tpr-prefix">₹</span>}
                      <input
                        type="number"
                        min="0"
                        className="tpr-input"
                        value={simulatorInput}
                        onChange={(e) => setSimulatorInput(e.target.value)}
                      />
                      {baseMeta.unit === "area" && <span className="tpr-suffix">चौ.फूट</span>}
                    </div>
                  </label>

                  {simulationResult && (
                    <div className="tpr-sim-result-badge">
                      एकूण आकारणी कर: <strong>₹{simulationResult.total.toLocaleString("en-IN")}</strong>
                    </div>
                  )}
                </div>

                {simulationResult && simulationResult.steps && (
                  <div className="tpr-sim-steps">
                    {simulationResult.steps.map((st, sIdx) => (
                      <div key={sIdx} className="tpr-sim-step-line">
                        • {st}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* 6. EXPRESSION FORM */}
          {formulaType === "expression" && (
            <div className="tpr-fields-group">
              <label className="tpr-label">
                सविकल्प गणिती सूत्र (Custom Math Expression) *
                <textarea
                  className="tpr-textarea"
                  rows={3}
                  placeholder={
                    isBusiness
                      ? "उदा. annual_turnover * 0.005 + (employee_count * 50)"
                      : "उदा. built_up_area_sqft * 2.50 * usage_factor"
                  }
                  value={expression}
                  onChange={(e) => setExpression(e.target.value)}
                  required
                />
              </label>

              <div className="tpr-variables-bar">
                <span className="tpr-var-label">स्थानिक चलने (Click to insert):</span>
                {(isBusiness
                  ? [
                      "annual_turnover",
                      "premise_area_sqft",
                      "employee_count",
                      "signage_area_sqft",
                      "active_location_count",
                    ]
                  : [
                      "built_up_area_sqft",
                      "annual_rental_value",
                      "assessed_value",
                      "carpet_area_sqft",
                      "land_area_sqft",
                      "usage_factor",
                      "construction_factor",
                      "age_factor",
                    ]
                ).map((v) => (
                  <button
                    key={v}
                    type="button"
                    className="tpr-var-chip"
                    onClick={() => insertVariable(v)}
                  >
                    + {v}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
